import { DIAMONDS } from "../data/balance";
import type { ArenaBounds, Diamond, DiamondType, Player, Point } from "./models";
import { specialDiamondAvailable } from "./SurvivalDifficulty";

const standardTypes: DiamondType[] = [
  "SHARD",
  "CUT_DIAMOND",
  "FLAWLESS_DIAMOND",
  "SYNDICATE_GEM",
  "CROWN_JEWEL",
];
const specialTypes: DiamondType[] = [
  "CLEAN_DIAMOND",
  "FREEZE_DIAMOND",
  "SHIELD_DIAMOND",
  "TIME_DIAMOND",
];

export class CollisionSystem {
  overlaps(player: Player, diamond: Diamond, pickupRadiusBonus = 0): boolean {
    return Math.hypot(
      player.position.x - diamond.position.x,
      player.position.y - diamond.position.y,
    ) < player.radius + diamond.radius + pickupRadiusBonus;
  }
}

export class SpawnSystem {
  private nextId = 1;

  constructor(private readonly random: () => number = Math.random) {}

  create(
    bounds: ArenaBounds,
    alertLevel = 0,
    freezeOnCooldown = false,
    rareChanceBonus = 0,
    allowedTypes?: DiamondType[],
  ): Diamond {
    const type = this.pickType(alertLevel, freezeOnCooldown, rareChanceBonus, allowedTypes);
    return {
      id: this.nextId++,
      type,
      position: this.pointInside(bounds),
      radius: DIAMONDS[type].size + 8,
      pulse: this.random() * Math.PI * 2,
    };
  }

  respawn(
    diamond: Diamond,
    bounds: ArenaBounds,
    avoid?: Point,
    minimumDistance = 110,
    alertLevel = 0,
    freezeOnCooldown = false,
    rareChanceBonus = 0,
    allowedTypes?: DiamondType[],
  ): void {
    diamond.type = this.pickType(alertLevel, freezeOnCooldown, rareChanceBonus, allowedTypes);
    diamond.position = this.pointInside(bounds, avoid, minimumDistance);
    diamond.radius = DIAMONDS[diamond.type].size + 8;
    diamond.pulse = this.random() * Math.PI * 2;
  }

  pointInside(bounds: ArenaBounds, avoid?: Point, minimumDistance = 0): Point {
    let point: Point = { x: bounds.left, y: bounds.top };
    for (let attempt = 0; attempt < 8; attempt += 1) {
      point = {
        x: bounds.left + this.random() * (bounds.right - bounds.left),
        y: bounds.top + this.random() * (bounds.bottom - bounds.top),
      };
      if (!avoid || Math.hypot(point.x - avoid.x, point.y - avoid.y) >= minimumDistance) break;
    }
    return point;
  }

  private pickType(
    alertLevel = 0,
    freezeOnCooldown = false,
    rareChanceBonus = 0,
    allowedTypes?: DiamondType[],
  ): DiamondType {
    const eligibleSpecials = specialTypes.filter((type) =>
      specialDiamondAvailable(type, alertLevel, freezeOnCooldown));
    const eligibleTypes = [...standardTypes, ...eligibleSpecials]
      .filter((type) => !allowedTypes || allowedTypes.includes(type));
    const fallbackTypes = allowedTypes?.length ? allowedTypes : standardTypes;
    const adjustedWeight = (type: DiamondType): number => {
      if (["FLAWLESS_DIAMOND", "SYNDICATE_GEM", "CROWN_JEWEL"].includes(type)) {
        return DIAMONDS[type].weight * (1 + rareChanceBonus * 5);
      }
      return DIAMONDS[type].weight;
    };
    const weightedTypes = eligibleTypes.length ? eligibleTypes : fallbackTypes;
    const totalWeight = weightedTypes.reduce((sum, type) => sum + adjustedWeight(type), 0);
    let roll = this.random() * totalWeight;
    for (const type of weightedTypes) {
      roll -= adjustedWeight(type);
      if (roll <= 0) return type;
    }
    return "SHARD";
  }

  selectType(alertLevel = 0, freezeOnCooldown = false, rareChanceBonus = 0, allowedTypes?: DiamondType[]): DiamondType {
    return this.pickType(alertLevel, freezeOnCooldown, rareChanceBonus, allowedTypes);
  }
}
